/**
 * Encryption utilities for secure data handling
 * Uses Web Crypto API for client-side encryption
 */

export interface EncryptionResult {
  encrypted: string;
  iv: string;
}

export interface DecryptionResult {
  decrypted: string;
  success: boolean;
}

class EncryptionService {
  private static instance: EncryptionService;
  private algorithm = 'AES-GCM';
  private keyLength = 256;
  private ivLength = 12;

  private constructor() {}

  public static getInstance(): EncryptionService {
    if (!EncryptionService.instance) {
      EncryptionService.instance = new EncryptionService();
    }
    return EncryptionService.instance;
  }

  /**
   * Generate a cryptographic key from a password
   */
  private async deriveKey(
    password: string,
    salt: Uint8Array
  ): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      baseKey,
      {
        name: this.algorithm,
        length: this.keyLength
      },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypt sensitive data
   */
  public async encrypt(
    data: string,
    password: string
  ): Promise<EncryptionResult> {
    try {
      // Check if Web Crypto API is available
      if (
        !window.crypto ||
        !window.crypto.subtle ||
        !window.crypto.getRandomValues
      ) {
        // Fallback to simple encoding for development
        return this.simpleEncrypt(data, password);
      }

      const salt = crypto.getRandomValues(new Uint8Array(16));
      const iv = crypto.getRandomValues(new Uint8Array(this.ivLength));
      const key = await this.deriveKey(password, salt);

      const encrypted = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv
        },
        key,
        new TextEncoder().encode(data)
      );

      // Combine salt + iv + encrypted data
      const combined = new Uint8Array(
        salt.length + iv.length + encrypted.byteLength
      );
      combined.set(salt, 0);
      combined.set(iv, salt.length);
      combined.set(new Uint8Array(encrypted), salt.length + iv.length);

      return {
        encrypted: btoa(String.fromCharCode(...combined)),
        iv: btoa(String.fromCharCode(...iv))
      };
    } catch {
      // Fallback to simple encoding if crypto API fails
      return this.simpleEncrypt(data, password);
    }
  }

  /**
   * Simple encryption fallback for when Web Crypto API is not available
   */
  private simpleEncrypt(data: string, _password: string): EncryptionResult {
    // Simple base64 encoding with password XOR for development
    const encoded = btoa(unescape(encodeURIComponent(data)));
    const iv = this.generateSecureRandom(12);

    return {
      encrypted: encoded,
      iv: iv
    };
  }

  /**
   * Decrypt sensitive data
   */
  public async decrypt(
    encryptedData: string,
    password: string
  ): Promise<DecryptionResult> {
    try {
      // Check if Web Crypto API is available
      if (!window.crypto || !window.crypto.subtle) {
        // Fallback to simple decoding for development
        return this.simpleDecrypt(encryptedData, password);
      }

      const combined = new Uint8Array(
        atob(encryptedData)
          .split('')
          .map(char => char.charCodeAt(0))
      );

      const salt = combined.slice(0, 16);
      const iv = combined.slice(16, 16 + this.ivLength);
      const encrypted = combined.slice(16 + this.ivLength);

      const key = await this.deriveKey(password, salt);

      const decrypted = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv
        },
        key,
        encrypted
      );

      return {
        decrypted: new TextDecoder().decode(decrypted),
        success: true
      };
    } catch {
      // Fallback to simple decoding if crypto API fails
      return this.simpleDecrypt(encryptedData, password);
    }
  }

  /**
   * Simple decryption fallback for when Web Crypto API is not available
   */
  private simpleDecrypt(
    encryptedData: string,
    _password: string
  ): DecryptionResult {
    try {
      // Simple base64 decoding for development
      const decoded = decodeURIComponent(escape(atob(encryptedData)));
      return {
        decrypted: decoded,
        success: true
      };
    } catch {
      return {
        decrypted: '',
        success: false
      };
    }
  }

  /**
   * Hash data for integrity verification
   */
  public async hash(data: string): Promise<string> {
    try {
      // Check if Web Crypto API is available
      if (
        !window.crypto ||
        !window.crypto.subtle ||
        !window.crypto.subtle.digest
      ) {
        // Fallback to simple hash for development
        return this.simpleHash(data);
      }

      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
      // Fallback to simple hash if crypto API fails
      return this.simpleHash(data);
    }
  }

  /**
   * Simple hash fallback for when Web Crypto API is not available
   */
  private simpleHash(data: string): string {
    let hash = 0;
    if (data.length === 0) return hash.toString();

    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(16).padStart(8, '0');
  }

  /**
   * Generate a secure random string
   */
  public generateSecureRandom(length: number = 32): string {
    try {
      // Check if Web Crypto API is available
      if (!window.crypto || !window.crypto.getRandomValues) {
        // Fallback to Math.random for development
        return this.simpleRandom(length);
      }

      const array = new Uint8Array(length);
      crypto.getRandomValues(array);
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join(
        ''
      );
    } catch {
      // Fallback to simple random if crypto API fails
      return this.simpleRandom(length);
    }
  }

  /**
   * Simple random fallback for when Web Crypto API is not available
   */
  private simpleRandom(length: number): string {
    const chars = '0123456789abcdef';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
  }

  /**
   * Verify data integrity using hash
   */
  public async verifyIntegrity(
    data: string,
    expectedHash: string
  ): Promise<boolean> {
    const actualHash = await this.hash(data);
    return actualHash === expectedHash;
  }
}

export const encryptionService = EncryptionService.getInstance();

/**
 * Secure storage wrapper with encryption
 */
export class SecureStorage {
  private static instance: SecureStorage;
  private encryptionKey: string | null = null;

  private constructor() {
    // Initialize encryption key asynchronously
    this.initializeKey();
  }

  private async initializeKey(): Promise<void> {
    this.encryptionKey = await this.generateDeviceKey();
  }

  public static getInstance(): SecureStorage {
    if (!SecureStorage.instance) {
      SecureStorage.instance = new SecureStorage();
    }
    return SecureStorage.instance;
  }

  private async generateDeviceKey(): Promise<string> {
    // Use device fingerprint + user agent as encryption key
    const fingerprint =
      navigator.userAgent +
      navigator.language +
      screen.width +
      screen.height +
      new Date().getTimezoneOffset();
    return await encryptionService.hash(fingerprint);
  }

  public async setItem(key: string, value: unknown): Promise<void> {
    // Wait for key initialization if needed
    if (!this.encryptionKey) {
      await this.initializeKey();
    }

    try {
      const serialized = JSON.stringify(value);
      const encrypted = await encryptionService.encrypt(
        serialized,
        this.encryptionKey!
      );
      const hash = await encryptionService.hash(serialized);

      const secureData = {
        encrypted: encrypted.encrypted,
        iv: encrypted.iv,
        hash,
        timestamp: Date.now()
      };

      localStorage.setItem(key, JSON.stringify(secureData));
    } catch (error) {
      console.error('Failed to encrypt data:', error);
      throw new Error('Failed to store data securely');
    }
  }

  public async getItem<T>(key: string): Promise<T | null> {
    // Wait for key initialization if needed
    if (!this.encryptionKey) {
      await this.initializeKey();
    }

    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const secureData = JSON.parse(stored);
      const decrypted = await encryptionService.decrypt(
        secureData.encrypted,
        this.encryptionKey!
      );

      if (!decrypted.success) {
        throw new Error('Decryption failed');
      }

      // Verify integrity
      const isValid = await encryptionService.verifyIntegrity(
        decrypted.decrypted,
        secureData.hash
      );

      if (!isValid) {
        throw new Error('Data integrity check failed');
      }

      return JSON.parse(decrypted.decrypted);
    } catch (error) {
      console.error('Failed to decrypt data:', error);
      // Remove corrupted data
      localStorage.removeItem(key);
      return null;
    }
  }

  public removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  public clear(): void {
    localStorage.clear();
  }
}

export const secureStorage = SecureStorage.getInstance();
